import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from './axiosInstance';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import BusinessIcon from '@mui/icons-material/Business';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useTheme } from '@mui/material/styles';

import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import MicrowaveOutlinedIcon from '@mui/icons-material/MicrowaveOutlined';
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined';
import QuestionMarkOutlinedIcon from '@mui/icons-material/QuestionMarkOutlined';

import { useSnackbar } from '../providers/SnackbarProvider';
import InlineEdit from './ui/InlineEdit';
import CopyToClipboard from './ui/CopyToClipboard';
import { isValidEmail } from '../utils/validation';
import ActivityHub from './ActivityHub';
import FileRail from './ui/FileRail';
import SectionCard from './ui/SectionCard';
import MilestoneTracker from './ui/MilestoneTracker';
import StaleBanner from './ui/StaleBanner';
import LostReasonDialog from './ui/LostReasonDialog';
import { DetailSkeleton } from './ui/PageSkeleton';
import { useBreadcrumbs } from './BreadcrumbBar';
import { usePipelineConfig } from '../providers/PipelineConfigProvider';
import {
  salutationOptions, titleOptions, sourceOptions, scoreOptions,
} from '../constants/leadOptions';

const initials = (fn, ln) => `${fn?.[0] ?? ''}${ln?.[0] ?? ''}`.toUpperCase() || '?';

/**
 * Lead-Detailseite nach dem Zonen-Bauplan (Konzept 2.2):
 *   A Kopfzeile   – Identität + die eine Hauptaktion + „⋯"-Menü
 *   B Phasenband  – MilestoneTracker in voller Breite, Checkliste dockt an
 *   C Stammdaten & Ablage (linke Spalte) – Felder, Verknüpft, Dateien
 *   D Aktivitäten (rechte Spalte, das Rückgrat) – Composer · Geplant · Verlauf
 * Ruhe-Prinzip: keine Karten-in-Karten — Weißraum, Beschriftungen und eine
 * feine Trennlinie zwischen den Spalten gliedern die Seite.
 */
const LeadDetails = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  // Pipeline-Konfiguration (B3): Phasen, Checkliste und Labels aus dem Admin
  const { phasesFor, lostFor, statusLabelsFor } = usePipelineConfig();
  const leadPhases = phasesFor('lead');
  const leadLost = lostFor('lead');
  const leadStatusLabels = statusLabelsFor('lead');
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [attachmentCount, setAttachmentCount] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [lostDialogOpen, setLostDialogOpen] = useState(false);
  const [lastActivityAt, setLastActivityAt] = useState(null);

  useBreadcrumbs(
    lead ? [{ label: 'Leads', path: '/leads' }, { label: `${lead.firstName} ${lead.lastName}` }] : null
  );

  const patchLead = async (data) => {
    try {
      const response = await axiosInstance.patch(`/leads/${id}/`, data);
      setLead(response.data);
      showSnackbar('Erfolgreich aktualisiert', 'success');
    } catch (error) {
      console.error('Error updating lead:', error);
      showSnackbar('Fehler beim Aktualisieren', 'error');
      throw error;
    }
  };
  const handleFieldUpdate = (field, value) => patchLead({ [field]: value });

  useEffect(() => {
    const loadData = async () => {
      try {
        const leadRes = await axiosInstance.get(`/leads/${id}/`);
        setLead(leadRes.data);
      } catch (error) {
        console.error('Error loading lead detail', error);
      }
    };
    loadData();
  }, [id]);

  const handleConvertToContact = async () => {
    if (!lead) return;
    const hasCompany = lead.company && lead.company.trim() !== '';
    const confirmMessage = hasCompany
      ? `Lead konvertieren?\n\nEs wird erstellt:\n• 1 Contact: ${lead.firstName} ${lead.lastName}\n• 1 Account: ${lead.company}\n\nFortfahren?`
      : `Lead konvertieren?\n\nEs wird erstellt:\n• 1 Contact: ${lead.firstName} ${lead.lastName}\n\n(Kein Account, da keine Firma angegeben)\n\nFortfahren?`;
    if (!window.confirm(confirmMessage)) return;
    try {
      const response = await axiosInstance.post(`/leads/${lead.id}/convert/`);
      const updatedLead = response.data.lead || response.data;
      setLead(updatedLead);
      const accountInfo = response.data.account_id
        ? `\nAccount ${response.data.account_created ? 'erstellt' : 'verknüpft'}: #${response.data.account_id}`
        : '';
      showSnackbar(`Erfolgreich konvertiert — Contact #${response.data.contact_id}${accountInfo}`, 'success');
    } catch (error) {
      console.error('Error converting lead:', error);
      showSnackbar(error.response?.data?.error || 'Fehler beim Konvertieren des Leads.', 'error');
    }
  };

  const openLostDialog = () => {
    setMenuAnchor(null);
    setLostDialogOpen(true);
  };

  const getScoreChip = (score) => {
    const map = {
      Cold: { color: theme.palette.leadScore.cold, label: 'Kalt', icon: <AcUnitOutlinedIcon /> },
      Warm: { color: theme.palette.leadScore.warm, label: 'Warm', icon: <MicrowaveOutlinedIcon /> },
      Hot: { color: theme.palette.leadScore.hot, label: 'Heiß', icon: <LocalFireDepartmentOutlinedIcon /> },
    };
    const s = map[score] || { color: theme.palette.leadScore.unknown, label: 'Unbekannt', icon: <QuestionMarkOutlinedIcon /> };
    return (
      <Chip size="small" icon={React.cloneElement(s.icon, { style: { color: s.color, fontSize: '1rem' } })} label={s.label}
        sx={{ bgcolor: s.color + '20', color: s.color, fontWeight: 600 }} />
    );
  };

  if (!lead) return <DetailSkeleton />;

  const converted = lead.is_converted;
  const isDeclined = !!leadLost && lead.status === leadLost.key;
  const curPhase = leadPhases.find((p) => p.key === lead.status);
  const doneSet = new Set((lead.checklist_state && curPhase && lead.checklist_state[curPhase.key]) || []);
  const checklistItems = curPhase ? curPhase.checklist.map((item) => ({ label: item, done: doneSet.has(item) })) : [];
  const toggleChecklistItem = (label) => {
    if (!curPhase) return;
    const next = new Set(doneSet);
    next.has(label) ? next.delete(label) : next.add(label);
    handleFieldUpdate('checklist_state', { ...(lead.checklist_state || {}), [curPhase.key]: [...next] });
  };

  // Ein Feld-Zeile (Label + InlineEdit) — Render-Funktion, kein eigener Component (verhindert Remount/Fokusverlust)
  // Unterhalb von lg ist die Randspalte schmal → Label über dem Wert (gestapelt),
  // ab lg nebeneinander. Verhindert Buchstaben-Umbrüche bei langen Werten.
  const fieldRow = (label, field, opts = {}) => (
    <Box key={field} sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '88px 1fr', md: '1fr', lg: '88px 1fr' },
      alignItems: 'center', columnGap: 1, rowGap: 0, py: { xs: 0.3, md: 0.55, lg: 0.3 },
    }}>
      <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
      <InlineEdit
        value={lead?.[field]}
        onSave={(v) => handleFieldUpdate(field, v)}
        selectOptions={opts.select}
        validate={opts.validate}
        disabled={converted}
      />
    </Box>
  );

  const railLabel = (label, mt = 3) => (
    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt, lineHeight: 1.6 }}>{label}</Typography>
  );

  return (
    <Box sx={{ pb: 3 }}>
      {/* A — Kopfzeile: Identität + die eine Hauptaktion + „⋯" (eigene Fläche, v2.4) */}
      <SectionCard sx={{ mb: 2.5 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ width: 58, height: 58, bgcolor: 'primary.light', color: 'primary.dark', fontWeight: 800, fontSize: '1.3rem' }}>
            {initials(lead.firstName, lead.lastName)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <Typography variant="h4">{lead.firstName} {lead.lastName}</Typography>
              {lead.score && getScoreChip(lead.score)}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
              {lead.company && (<><BusinessIcon sx={{ fontSize: 16 }} />{lead.company}{lead.position ? ` · ${lead.position}` : ''}</>)}
              {lead.city && (<><PlaceOutlinedIcon sx={{ fontSize: 16, ml: lead.company ? 1 : 0 }} />{lead.city}</>)}
            </Typography>
            {lead.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>{lead.email}</Typography>
                <CopyToClipboard text={lead.email} tooltip="E-Mail kopieren" size="small" iconSx={{ fontSize: 14, color: 'text.secondary' }} />
              </Box>
            )}
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {converted ? (
            <>
              {lead.converted_contact && <Button size="small" variant="outlined" onClick={() => navigate(`/contacts/${lead.converted_contact}`)}>→ Kontakt</Button>}
              {lead.converted_account && <Button size="small" variant="outlined" onClick={() => navigate(`/accounts/${lead.converted_account}`)}>→ Unternehmen</Button>}
            </>
          ) : (
            <>
              {lead.status === 'Closed Won' && (
                <Button variant="contained" onClick={handleConvertToContact}>In Kontakt umwandeln</Button>
              )}
              <IconButton aria-label="Weitere Aktionen" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                <MoreHorizIcon />
              </IconButton>
              <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                <MenuItem onClick={openLostDialog} disabled={isDeclined || !leadLost} sx={{ color: 'error.main' }}>
                  Als abgelehnt markieren
                </MenuItem>
              </Menu>
            </>
          )}
        </Stack>
      </Stack>
      </SectionCard>

      {/* B — Phasenband mit angedockter Checkliste */}
      <Box sx={{ mb: 3 }}>
        <MilestoneTracker
          phases={leadPhases}
          currentKey={lead.status}
          lost={leadLost}
          successKey={leadPhases[leadPhases.length - 1]?.key}
          sinceDate={lead.phase_changed_at}
          lostReason={lead.lost_reason}
          checklistItems={checklistItems}
          onToggleChecklistItem={toggleChecklistItem}
          onChangePhase={(key) => handleFieldUpdate('status', key)}
          disabled={converted}
        />
      </Box>

      {/* Inaktivitäts-Warnung — nur bei offenen (nicht konvertiert/abgelehnt) Leads */}
      {!converted && !isDeclined && (
        <StaleBanner lastActivityAt={lastActivityAt}
          hintDays={curPhase?.staleHintDays} warnDays={curPhase?.staleWarnDays} />
      )}

      {/* Zwei ruhige Flächen („Papier auf Tisch"): weiße Spalten auf dem getönten
          Seitengrund — klare Trennung, aber weiterhin nur eine Box-Ebene. */}
      <Grid container spacing={2.5} alignItems="flex-start">
        {/* C — Stammdaten & Ablage */}
        <Grid size={{ xs: 12, md: 4, lg: 3.5 }} order={{ xs: 2, md: 1 }}
          sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <SectionCard>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>Person</Typography>
          {fieldRow('Vorname', 'firstName', { validate: (v) => (!v?.trim() ? 'Vorname erforderlich' : null) })}
          {fieldRow('Nachname', 'lastName', { validate: (v) => (!v?.trim() ? 'Nachname erforderlich' : null) })}
          {fieldRow('Position', 'position')}
          {showAll && fieldRow('Anrede', 'salutation', { select: salutationOptions })}
          {showAll && fieldRow('Titel', 'title', { select: titleOptions })}

          {railLabel('Vertrieb')}
          {fieldRow('Herkunft', 'source', { select: sourceOptions })}
          {fieldRow('Score', 'score', { select: scoreOptions })}

          {railLabel('Unternehmen')}
          {fieldRow('Firma', 'company')}
          {fieldRow('Stadt', 'city')}

          {railLabel('Erreichbarkeit')}
          {fieldRow('E-Mail', 'email', { validate: (v) => (v && !isValidEmail(v) ? 'Ungültige E-Mail' : null) })}
          {fieldRow('Telefon', 'phone')}
          {showAll && fieldRow('Mobil', 'mobile')}
          {showAll && fieldRow('Straße', 'street')}
          {showAll && fieldRow('PLZ', 'zip_code')}
          {showAll && fieldRow('Webseite', 'website')}

          {railLabel('Notizen')}
          <InlineEdit value={lead?.notes} onSave={(v) => handleFieldUpdate('notes', v)} multiline rows={4} disabled={converted} />
          <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{ mt: 1, px: 0.5 }}>
            {showAll ? 'Weitere Felder ausblenden ▴' : 'Alle Felder anzeigen ▾'}
          </Button>

          </SectionCard>

          {/* Verknüpft — generisch angelegt: heute Firma/Kontakt, künftig auch Cases u. a. */}
          <SectionCard>
          {railLabel('Verknüpft', 0)}
          {lead.company ? (
            <Box
              role={lead.converted_account ? 'button' : undefined}
              tabIndex={lead.converted_account ? 0 : -1}
              onClick={() => lead.converted_account && navigate(`/accounts/${lead.converted_account}`)}
              onKeyDown={(e) => { if (lead.converted_account && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); navigate(`/accounts/${lead.converted_account}`); } }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, py: 0.6,
                cursor: lead.converted_account ? 'pointer' : 'default',
                '&:hover': lead.converted_account ? { color: 'primary.main' } : {},
              }}
            >
              <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{lead.company}</Typography>
              {lead.converted_account && <Typography variant="caption" color="primary.main">öffnen →</Typography>}
            </Box>
          ) : null}
          {lead.converted_contact ? (
            <Box
              role="button" tabIndex={0}
              onClick={() => navigate(`/contacts/${lead.converted_contact}`)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/contacts/${lead.converted_contact}`); } }}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
            >
              <PersonOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{lead.firstName} {lead.lastName}</Typography>
              <Typography variant="caption" color="primary.main">Kontakt öffnen →</Typography>
            </Box>
          ) : null}
          {!lead.company && !lead.converted_contact && (
            <Typography variant="caption" color="text.secondary">Nach der Konvertierung erscheinen hier Kontakt &amp; Unternehmen.</Typography>
          )}

          </SectionCard>

          <SectionCard>
          {railLabel(attachmentCount != null ? `Dateien (${attachmentCount})` : 'Dateien', 0)}
          <FileRail relatedType="lead" relatedId={lead.id} onCountChange={setAttachmentCount} disabled={converted} />
          </SectionCard>
        </Grid>

        {/* D — Aktivitäten (das Rückgrat) */}
        <Grid size={{ xs: 12, md: 8, lg: 8.5 }} order={{ xs: 1, md: 2 }} sx={{ minWidth: 0 }}>
          <ActivityHub
            relatedType="lead"
            relatedId={id}
            phaseChangedAt={lead.phase_changed_at}
            phaseLabel={curPhase ? curPhase.label : (isDeclined ? leadLost.label : lead.status)}
            statusHistory={lead.status_history}
            statusLabels={leadStatusLabels}
            onMeta={({ lastActivityAt: la }) => setLastActivityAt(la)}
            disabled={converted}
          />
        </Grid>
      </Grid>

      <LostReasonDialog
        open={lostDialogOpen}
        onClose={() => setLostDialogOpen(false)}
        onConfirm={(reason) => { setLostDialogOpen(false); patchLead({ status: leadLost?.key, lost_reason: reason }); }}
      />
    </Box>
  );
};

export default LeadDetails;
